import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/UI/Button';
import { apiErrorMessage } from '@/utils/apiError';
import { humanize } from '@/components/Workflow/workflowSettingsShared';
import {
  useDecideApprovalMutation,
  type ApprovalRequestOut,
} from '@/redux/internal-services/workflowEngineApi';

interface Props {
  approval: ApprovalRequestOut;
  onOpenEngagement?: (engagementId: string) => void;
}

const STATUS_TAG: Record<string, string> = {
  pending: 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-400',
  approved: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400',
  rejected: 'bg-red-100 text-[#CF4148] dark:bg-red-900/30 dark:text-red-400',
  cancelled: 'bg-gray-100 text-gray-600 dark:bg-white/10 dark:text-gray-300',
};

const DetailRow: React.FC<{ label: string; children: React.ReactNode }> = ({ label, children }) => (
  <div className="flex items-start justify-between gap-4 py-2.5 border-b border-[#EDEDED] last:border-b-0">
    <p className="text-[10px] font-semibold text-ui-text3 uppercase tracking-wider">{label}</p>
    <div className="text-sm text-ui-text text-right">{children}</div>
  </div>
);

const ApprovalDetailPanel: React.FC<Props> = ({ approval, onOpenEngagement }) => {
  const { t } = useTranslation();
  const [decideApproval, { isLoading: deciding }] = useDecideApprovalMutation();
  const [rejecting, setRejecting] = useState(false);
  const [comment, setComment] = useState('');
  const [error, setError] = useState<string | null>(null);

  const isPending = approval.status === 'pending';

  const approve = async () => {
    setError(null);
    try {
      await decideApproval({ approvalId: approval.approval_request_id, decision: 'approved' }).unwrap();
    } catch (e) {
      setError(apiErrorMessage(e, t, t('workflow.decisionFailed', 'Could not record that decision.')));
    }
  };

  const reject = async () => {
    setError(null);
    try {
      await decideApproval({
        approvalId: approval.approval_request_id, decision: 'rejected', comments: comment.trim() || undefined,
      }).unwrap();
      setRejecting(false);
    } catch (e) {
      setError(apiErrorMessage(e, t, t('workflow.decisionFailed', 'Could not record that decision.')));
    }
  };

  return (
    <div className="rounded-xl border border-[#EDEDED] bg-ui-surface p-4 sm:p-6">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h1 className="text-lg font-semibold text-ui-text">{humanize(approval.approval_type)}</h1>
          <p className="mt-0.5 text-xs text-ui-text3">
            {t('workflow.approvalRequestId', 'Approval')} · {approval.approval_request_id}
          </p>
        </div>
        <span className={`shrink-0 rounded-full px-2.5 py-1 text-[10px] font-semibold uppercase tracking-wide ${STATUS_TAG[approval.status] ?? STATUS_TAG.pending}`}>
          {approval.status}
        </span>
      </div>

      <div className="mt-4">
        <DetailRow label={t('workflow.strategy', 'Strategy')}>{humanize(approval.approval_strategy)}</DetailRow>
        <DetailRow label={t('workflow.requestedAt', 'Requested')}>{new Date(approval.requested_at).toLocaleString()}</DetailRow>
        {approval.allowed_approver_roles.length > 0 && (
          <DetailRow label={t('workflow.allowedRoles', 'Allowed roles')}>
            {approval.allowed_approver_roles.map(humanize).join(', ')}
          </DetailRow>
        )}
        <DetailRow label={t('workflow.engagement', 'Engagement')}>
          <div className="flex items-center justify-end gap-2">
            <span className="truncate max-w-[140px] text-ui-text3 text-xs">{approval.engagement_id}</span>
            {onOpenEngagement && approval.engagement_id && (
              <button
                type="button"
                onClick={() => onOpenEngagement(approval.engagement_id)}
                className="shrink-0 text-[11px] font-medium text-brand-dark hover:underline"
              >
                {t('workflow.openEngagement', 'Open')}
              </button>
            )}
          </div>
        </DetailRow>
        {!isPending && (
          <>
            <DetailRow label={t('workflow.decidedAt', 'Decided')}>
              {approval.decided_at ? new Date(approval.decided_at).toLocaleString() : '—'}
            </DetailRow>
            {approval.decision_comments && (
              <DetailRow label={t('workflow.comments', 'Comments')}>{approval.decision_comments}</DetailRow>
            )}
          </>
        )}
      </div>

      {isPending && (
        <div className="mt-4">
          {rejecting ? (
            <div className="space-y-2">
              <textarea
                value={comment}
                onChange={(e) => setComment(e.target.value)}
                placeholder={t('workflow.rejectCommentPlaceholder', 'Reason (optional)')}
                rows={3}
                className="w-full rounded-lg border border-ui-border bg-ui-input px-2.5 py-1.5 text-sm text-ui-text focus:border-brand-dark focus:outline-none focus:ring-1 focus:ring-brand-dark"
              />
              <div className="flex justify-end gap-2">
                <Button variant="ghost" size="sm" onClick={() => { setRejecting(false); setComment(''); }}>
                  {t('workflow.cancel', 'Cancel')}
                </Button>
                <Button variant="destructive" size="sm" loading={deciding} onClick={reject}>
                  {t('workflow.confirmReject', 'Confirm reject')}
                </Button>
              </div>
            </div>
          ) : (
            <div className="flex justify-end gap-2">
              <Button variant="outline" size="sm" loading={deciding} onClick={() => setRejecting(true)}>
                {t('workflow.reject', 'Reject')}
              </Button>
              <Button size="sm" loading={deciding} onClick={approve}>
                {t('workflow.approve', 'Approve')}
              </Button>
            </div>
          )}
          {error && (
            <div className="mt-2 rounded-lg border border-[#CF4148]/40 bg-red-50 px-3 py-2 text-xs text-[#CF4148] dark:bg-red-900/20">
              {error}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default ApprovalDetailPanel;
